import React from "react";
import Lexical from "lexical";
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$patchStyleText} from "@lexical/selection";
import {DraggableCore, DraggableEventHandler} from "react-draggable";
import {DropMenu} from "../DropMenu";
import {ColorSelectorButton} from "../ColorSelector";
import {BoldButton} from "../BoldButton";
import {AlignmentButtons} from "../AlignmentButton";
import {
    TextStyle
} from "../../../models/CellContent/TextStyle";
import {useTranslation} from "react-i18next";
import {AlignmentType} from "./EditorCommon";
import {
    AlignStyleType,
    CHANGE_TEXT_STYLE_TYPE,
    TextStyleType,
    TOGGLE_TEXT_STYLE_TYPE
} from "../../../models/CellContent/TextStyleType";


type LexicalEditorToolbarProps = {
    isShow: boolean;
}

export const LexicalEditorToolbar = (props: LexicalEditorToolbarProps) => {
    const {t} = useTranslation();

    const [editor] = useLexicalComposerContext();
    const [activeEditor, setActiveEditor] = React.useState(editor);

    const [text_style, setTextStyle] = React.useState(TextStyle.createDefault());
    const [isShow, setIsShow] = React.useState<boolean>(props.isShow);
    const [position, setPosition] = React.useState({x: 0, y: 0});

    const toolbar_style: React.CSSProperties = isShow ? {
        //display: "block",
        transform: 'translate(' + position.x + 'px, ' + position.y + 'px)',
        opacity: 1,
        transition: 'opacity 0.15s ease-in',
    } : {
        display: "none",
        transform: 'scale(0)',
        opacity: 0,
        transition: 'opacity 0.15s ease-in',
    }
    React.useEffect(() => {
        if (props.isShow) {
            setIsShow(true);
        } else {
            setIsShow(false);
            setPosition({x: 0, y: -50});
        }
    }, [props.isShow])


    const updateToolbar = React.useCallback(() => {
        const selection = Lexical.$getSelection();
        if (selection && Lexical.$isRangeSelection(selection)) {
            setTextStyle(TextStyle.fromLexicalSelection(Lexical.$getRoot(), selection));
        }
    }, [activeEditor]);


    React.useEffect(() => {
        return editor.registerCommand(
            Lexical.SELECTION_CHANGE_COMMAND,
            (_payload, newEditor) => {
                updateToolbar();
                setActiveEditor(newEditor);
                return false;
            },
            Lexical.COMMAND_PRIORITY_HIGH,
        );
    }, [editor, updateToolbar]);

    const changeBlockStyle = React.useCallback((blockStyle: AlignmentType) => {
        return (event: React.MouseEvent): void => {
            event.preventDefault();

            if (blockStyle === 'align_left') {
                activeEditor.dispatchCommand(Lexical.FORMAT_ELEMENT_COMMAND, 'left');
            } else if (blockStyle === 'align_center') {
                activeEditor.dispatchCommand(Lexical.FORMAT_ELEMENT_COMMAND, 'center');
            } else if (blockStyle === 'align_right') {
                activeEditor.dispatchCommand(Lexical.FORMAT_ELEMENT_COMMAND, 'right');
            }

            setTextStyle(ts => ts.setAlignmentType(AlignStyleType.fromString(blockStyle)));
        }
    }, [activeEditor]);

    const changeInlineStyle = React.useCallback((style_type: CHANGE_TEXT_STYLE_TYPE, style_name: string) => {
        return (event: React.MouseEvent): void => {
            event.preventDefault();

            const patch = (new TextStyleType(style_type)).makeCssObject(style_name);

            activeEditor.update(() => {
                const selection = Lexical.$getSelection();
                if (Lexical.$isRangeSelection(selection)) {
                    $patchStyleText(selection, patch);

                    // toggleInlineStyle(bold) とは異なり、こちらは 選択中のテキストの状態に応じて ボタンの表示を切り替えている
                    // そのため、テキスト未選択状態では 表示は切り替わらないが、それはある意味正しい状態である。
                    //
                    // bold は RangeSelection.format に値が保持されるため、テキスト未選択状態でも 変更した状態が次の入力文字に引き継がれるけど
                    // font/size/color は RangeSelection に保持する場所が無く、引き継ぐ手段が無い (lexical v0.6.4 現状)
                    setTextStyle(TextStyle.fromLexicalSelection(Lexical.$getRoot(), selection));
                }
            });

        }
    }, [activeEditor]);

    const toggleInlineStyle = React.useCallback((style_type: TOGGLE_TEXT_STYLE_TYPE) => {
        return (event: React.MouseEvent): void => {
            event.preventDefault();

            activeEditor.dispatchCommand(Lexical.FORMAT_TEXT_COMMAND, style_type);

            if (style_type === 'bold') {
                setTextStyle(ts => ts.toggleWeight());
            }
        }
    }, [activeEditor]);


    const handleDrag: DraggableEventHandler = (e, coreData) => {
        setPosition({x: position.x + coreData.deltaX, y: position.y + coreData.deltaY});
    }

    return (
        <DraggableCore onMouseDown={(e) => e.preventDefault()} onDrag={handleDrag} handle=".grip">
            <div className="editor_toolbar" style={toolbar_style}
                 onMouseDown={(e) => e.preventDefault() /* preventBubblingUp */}
            >
                <span className="grip"/>
                <div className="tools">
                    <DropMenu containerClassName="letter-font"
                              label={text_style.FontLabel}
                              tooltip={t("フォント")}>
                        <ul className="header-dropdown-menu">
                            <li data-drop_menu="dummy" onClick={changeInlineStyle('font', 'sans_serif')}>
                                {t("ゴシック")}
                            </li>
                            <li data-drop_menu="dummy" onClick={changeInlineStyle('font', 'serif')}>
                                {t("明朝")}
                            </li>
                        </ul>
                    </DropMenu>
                    <DropMenu containerClassName="letter-size"
                              label={text_style.SizeLabel}
                              tooltip={t("文字サイズ")}>
                        <ul className="header-dropdown-menu">
                            <li data-drop_menu="dummy"
                                onClick={changeInlineStyle('size', 'small')}>{t("小")}</li>
                            <li data-drop_menu="dummy"
                                onClick={changeInlineStyle('size', 'normal')}>{t("標準")}</li>
                            <li data-drop_menu="dummy"
                                onClick={changeInlineStyle('size', 'medium')}>{t("大")}</li>
                            <li data-drop_menu="dummy"
                                onClick={changeInlineStyle('size', 'large')}>{t("特大")}</li>
                        </ul>
                    </DropMenu>

                    <ColorSelectorButton className={""} tooltip={t("テキストの色")}
                                         color={text_style.ColorCode}
                                         onChange={((color, e) => (changeInlineStyle('color', color))(e))}/>


                    <BoldButton className={""} tooltip={t("太字")}
                                active={text_style.IsBold}
                                onClick={toggleInlineStyle('bold')}/>

                    <AlignmentButtons
                        activeType={text_style.AlignmentType}
                        onClick={(alignment_type, e) => changeBlockStyle(alignment_type)(e)}
                    />
                </div>
            </div>
        </DraggableCore>
    );


}
